import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Request, RequestHistory } from 'src/app/request-v2/models/request.model';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

interface HistoryEntry {
    id: string;
    type: string;
    icon: string;
    isAi: boolean;
    action: string;
    detail?: string;
    actor?: string;
    time: string;
    date: Date;
}

interface HistoryDayGroup {
    dateKey: string;
    dayLabel: string;
    entries: HistoryEntry[];
}

@Component({
    selector: 'app-request-history',
    templateUrl: './request-history.component.html',
    styleUrls: ['./request-history.component.scss'],
})
export class RequestHistoryComponent implements OnInit, OnDestroy, OnChanges {
    @Input() request: Request;
    dayGroups: HistoryDayGroup[] = [];

    private currentLang: string = 'fr';
    private langSub: Subscription;

    constructor(private translate: TranslateService) {}

    ngOnInit(): void {
        this.currentLang = this.translate.currentLang || 'fr';
        this.langSub = this.translate.onLangChange.subscribe((e: LangChangeEvent) => {
            this.currentLang = e.lang;
            this.buildDayGroups();
        });
        this.buildDayGroups();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.request) {
            this.buildDayGroups();
        }
    }

    ngOnDestroy(): void {
        this.langSub?.unsubscribe();
    }

    private buildDayGroups(): void {
        if (!this.request?.history?.length) {
            this.dayGroups = [];
            return;
        }

        const entries: HistoryEntry[] = this.request.history.map((h: RequestHistory) => this.mapEntry(h)).sort((a, b) => b.date.getTime() - a.date.getTime());

        const groupMap = new Map<string, HistoryDayGroup>();
        const order: string[] = [];

        for (const entry of entries) {
            const d = entry.date;
            const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            if (!groupMap.has(dateKey)) {
                groupMap.set(dateKey, { dateKey, dayLabel: this.getDayLabel(dateKey), entries: [] });
                order.push(dateKey);
            }
            groupMap.get(dateKey).entries.push(entry);
        }

        this.dayGroups = order.map((k) => groupMap.get(k));
    }

    private mapEntry(h: RequestHistory): HistoryEntry {
        const date = new Date(h.date || h.createdAt);
        const { action, detail } = this.extractActionDetail(h);
        const isAi = !!(h.metadata?.isAi || h.type?.toUpperCase().includes('AI'));
        const actor = h.user ? `${h.user.firstName} ${h.user.lastName}`.trim() : undefined;
        const locale = this.currentLang === 'fr' ? 'fr-CA' : 'en-CA';
        const hour12 = this.currentLang !== 'fr';
        const time = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12 });

        return { id: h._id, type: h.type, icon: this.getIconForType(h.type), isAi, action, detail, actor: isAi ? undefined : actor, time, date };
    }

    private extractActionDetail(h: RequestHistory): { action: string; detail?: string } {
        const translated = h.actionKey ? this.translate.instant(h.actionKey) : null;
        const hasTranslation = translated && translated !== h.actionKey;

        if (hasTranslation) {
            const detail = h.message && h.message !== translated ? h.message : undefined;
            return { action: translated, detail };
        }

        if (h.message) {
            const colonIdx = h.message.indexOf(': ');
            if (colonIdx > 0) {
                return { action: h.message.substring(0, colonIdx), detail: h.message.substring(colonIdx + 2) };
            }
            return { action: h.message };
        }

        return { action: h.actionKey || h.type };
    }

    private getIconForType(type: string): string {
        if (!type) return 'info';
        const t = type.toUpperCase();
        if (t.includes('LINK')) return 'link';
        if (t.includes('STATUS')) return 'adjust';
        if (t.includes('COMMENT') || t.includes('MESSAGE')) return 'chat_bubble_outline';
        if (t.includes('SUBTASK') || t.includes('TASK')) return 'checklist';
        if (t.includes('ATTACH') || t.includes('FILE')) return 'attach_file';
        if (t.includes('CREATED') || t.includes('CREATION')) return 'add_circle_outline';
        if (t.includes('ASSIGN')) return 'person_add';
        if (t.includes('TAG')) return 'label';
        if (t.includes('CATEGORY')) return 'folder_open';
        if (t.includes('NOTE')) return 'sticky_note_2';
        if (t.includes('UPDATE') || t.includes('EDIT') || t.includes('CHANGE')) return 'edit_note';
        return 'info';
    }

    private getDayLabel(dateKey: string): string {
        const today = new Date();
        const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const yesterdayKey = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;

        const [y, m, d] = dateKey.split('-').map(Number);
        const locale = this.currentLang === 'fr' ? 'fr-CA' : 'en-CA';
        const formatted = new Date(y, m - 1, d).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });

        if (dateKey === todayKey) return `${this.translate.instant('request_v2.history.today')} · ${formatted}`;
        if (dateKey === yesterdayKey) return `${this.translate.instant('request_v2.history.yesterday')} · ${formatted}`;
        return formatted;
    }
}
